import { useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiControlledTextField,
  MiMenu,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  OPTION_STATUS,
} from "@miplatform/constants";
import {
  IAreaTypeResponse,
  IBidOptionMaterial,
  IBidOptionResponse,
  IMaterialsResponse,
  IMergedBidArea,
} from "@miplatform/types";
import { mdiCheck, mdiClose, mdiPencilOutline, mdiPlus, mdiSync } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../../components/CollapsibleIcon/CollapsibleIcon";
import { BidMaterialOption } from "../BidMaterialOption/BidMaterialOption";
import { useBidAreaOptionsViewModel } from "./useBidAreaOptionsViewModel";

export function BidAreaOption({
  area,
  areaType,
  planOptionMaterials = [],
  isPreview,
  isEditingOption = false,
  checkedAreas,
  showCategoryFilter,
  openAreaDrawer,
  showLinkedItems,
  linkedMaterialDetails,
  optionData,
  setOptionData,
  handleOpenItemDrawer = () => {},
  handleOpenReplaceItemModal,
}: BidAreaOptionProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const { classes, theme, cx } = useStyles();
  const { control, watch, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      label: area.label || "",
    },
  });
  const { label } = watch();

  const {
    handleRemoveAreaFromOption,
    handleUndoRemoveAreaFromOption,
    handleRemoveItemFromArea,
    handleRemoveAllItemsFromArea,
    handleReturnItemToPlan,
    handleReturnAllItemsToPlan,
    handleUpdateItemQuantity,
  } = useBidAreaOptionsViewModel({ optionData, setOptionData });

  const isAreaSelected = checkedAreas.some((c) => c.id === area.id);
  const isDisabled = showCategoryFilter || openAreaDrawer;

  const areaMaterials = area.materials as IBidOptionMaterial[];

  const areaStatus = area.optionStatus;
  const isAreaRemoved = areaStatus === OPTION_STATUS.REMOVED;
  const isAreaAdded = areaStatus === OPTION_STATUS.ADDED;
  const isAreaExisting = areaStatus === OPTION_STATUS.EXISTING;
  const areaHasNoMaterials = areaMaterials?.length === 0;
  const someMaterialsRemoved = areaMaterials?.some(
    (m) => m.optionStatus === OPTION_STATUS.REMOVED
  );
  const allMaterialsRemoved = areaMaterials?.every(
    (m) => m.optionStatus === OPTION_STATUS.REMOVED
  );
  const isExistingAreaWithRemovedItems = someMaterialsRemoved && isAreaExisting;
  const hideActions = isAreaAdded && isPreview;

  const getLeftAreaActions = () => {
    if (isAreaRemoved) {
      return (
        <MiButton
          title="Undo Remove"
          variant={BUTTON_VARIANTS.TEXT}
          onClick={() => handleUndoRemoveAreaFromOption(area)}
          disabled={isDisabled}
          size="small"
        />
      );
    }
    if (isAreaAdded || isAreaExisting) {
      return (
        <MiButton
          title="Remove Area"
          variant={BUTTON_VARIANTS.TEXT}
          color={BUTTON_COLORS.ERROR}
          onClick={() => handleRemoveAreaFromOption(area)}
          disabled={isDisabled}
          size="small"
        />
      );
    }
    return null;
  };

  const getRightAreaActions = () => {
    if (isAreaRemoved) {
      return null;
    }
    if (isAreaAdded || isAreaExisting) {
      return (
        <Box className={classes.areaActions}>
          {isExistingAreaWithRemovedItems && allMaterialsRemoved ? (
            <MiButton
              title="Return All Items"
              variant={BUTTON_VARIANTS.TEXT}
              onClick={() => handleReturnAllItemsToPlan(area)}
              disabled={isDisabled}
              size="small"
            />
          ) : (
            <MiButton
              title="Remove All Items"
              variant={BUTTON_VARIANTS.TEXT}
              color={BUTTON_COLORS.ERROR}
              onClick={() => handleRemoveAllItemsFromArea(area)}
              disabled={isDisabled || areaHasNoMaterials}
              size="small"
            />
          )}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ margin: theme.spacing(2, 0) }}
          />
          <MiButton
            title="Add Items"
            variant={BUTTON_VARIANTS.TEXT}
            startIcon={{ path: mdiPlus, iconSize: 0.8 }}
            onClick={() => handleOpenItemDrawer(area)}
            disabled={openAreaDrawer}
            size="small"
          />
        </Box>
      );
    }
    return null;
  };

  const handleSaveLabel = () => {
    setOptionData((prev) => ({
      ...prev,
      planAreas: prev.planAreas.map((a) =>
        a.id === area.id ? { ...a, label } : a
      ),
    }));
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    reset({ label: area.label || "" });
    setIsEditingName(false);
  };

  const getCardMenu = (
    areaMaterial: IBidOptionMaterial,
    item: IMaterialsResponse
  ) => {
    const isMaterialRemoved =
      areaMaterial.optionStatus === OPTION_STATUS.REMOVED;
    // replace is only allowed for existing/added items while editing an option
    if (!isEditingOption || isAreaRemoved || isMaterialRemoved) return [];
    return [
      {
        label: "Replace Item",
        icon: mdiSync,
        action: () =>
          handleOpenReplaceItemModal({
            item: {
              ...item,
              materialId: areaMaterial.materialId,
              suppliedByBuilder: areaMaterial.suppliedByBuilder,
              quantity: areaMaterial.quantity,
            },
            area,
          }),
      },
    ];
  };

  return (
    <Accordion
      expanded={isExpanded}
      disableGutters
      className={cx(
        classes.accordion,
        isAreaAdded && classes.isAdded,
        isAreaRemoved && classes.isRemoved,
        isAreaSelected && classes.activeBorder,
        isPreview && classes.previewBox
      )}
    >
      <AccordionSummary
        className={cx(
          classes.summary,
          isAreaRemoved && classes.isRemoved,
          isAreaAdded && classes.isAdded
        )}
      >
        <Box className={classes.headerBox}>
          <Box className={classes.rightHeaderBox}>
            {isEditingName ? (
              <MiControlledTextField
                name="label"
                label={isEditingName ? areaType?.name : ""}
                formControl={control}
                disabled={!isEditingName}
                readOnly={!isEditingName}
                className={cx(
                  classes.labelInput,
                  isAreaRemoved && classes.strikethrough
                )}
              />
            ) : (
              <Typography className={classes.areaLabel}>{label}</Typography>
            )}
            {isEditingName && (
              <Box className={classes.cancelSaveBox}>
                <MiButton
                  startIcon={{
                    path: mdiClose,
                    iconSize: 0.7,
                    iconColor: theme.palette.red.main,
                  }}
                  size="small"
                  onClick={handleCancelEdit}
                />
                <MiButton
                  startIcon={{
                    path: mdiCheck,
                    iconSize: 0.7,
                    iconColor: theme.palette.green.main,
                  }}
                  size="small"
                  onClick={handleSaveLabel}
                />
              </Box>
            )}
            {isEditingOption && !isEditingName && !hideActions && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ margin: theme.spacing(2, 0) }}
                />
                {getLeftAreaActions()}
              </>
            )}
          </Box>
          <Box className={classes.leftHeaderBox}>
            {!isEditingName && !hideActions && (
              <>
                {isEditingOption && getRightAreaActions()}
                <CollapsibleIcon
                  isCollapsed={isExpanded}
                  onCollapse={() => setIsExpanded((prev) => !prev)}
                />
                {isEditingOption && isAreaAdded && (
                  <MiMenu
                    disabled={isDisabled}
                    data={[
                      {
                        label: "Edit Area Name",
                        icon: mdiPencilOutline,
                        action: () => setIsEditingName(true),
                      },
                    ]}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {areaMaterials?.map((am, index) => {
          const item = planOptionMaterials.find((m) => m.id === am.materialId);
          if (!item) return null;

          return (
            <BidMaterialOption
              key={am.materialId + am.optionStatus}
              item={item}
              areaMaterial={am}
              area={area}
              isLast={index === areaMaterials.length - 1}
              isDisabled={isDisabled}
              showLinkedItems={showLinkedItems}
              linkedMaterialDetails={linkedMaterialDetails}
              isAreaRemoved={isAreaRemoved}
              isAreaExisting={isAreaExisting}
              isEditingOption={isEditingOption}
              menuData={getCardMenu(am, item)}
              handleRemoveItemFromArea={handleRemoveItemFromArea}
              handleReturnItemToPlan={handleReturnItemToPlan}
              handleUpdateItemQuantity={handleUpdateItemQuantity}
            />
          );
        })}
        {areaHasNoMaterials && (
          <Box className={classes.emptyMaterialsContainer}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              padding={theme.spacing(1)}
            >
              Add items to the Area to begin creating a modification.
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    boxShadow: "none",
    "&::before": { content: "none" },
  },
  previewBox: {
    opacity: 0.5,
  },
  activeBorder: {
    border: `2px solid ${theme.palette.primary.accent}`,
  },
  isAdded: {
    backgroundColor: theme.palette.green.light,
  },
  isRemoved: {
    backgroundColor: theme.palette.red.light,
  },
  summary: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 32,
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    "& .MuiAccordionSummary-content": {
      margin: 0,
      cursor: "default",
    },
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  rightHeaderBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  labelInput: {
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1),
      fontSize: theme.fontSize.medium,
    },
  },
  areaActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  areaLabel: {
    padding: theme.spacing(1),
    fontSize: theme.fontSize.medium,
  },
  strikethrough: {
    "& .MuiOutlinedInput-root .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.red.dark,
      textDecoration: "line-through",
    },
  },
  leftHeaderBox: {
    display: "flex",
    alignItems: "center",
  },
  cancelSaveBox: {
    display: "flex",
    gap: 1,
  },
  accordionDetails: {
    padding: theme.spacing(0.5, 1),
  },
  emptyMaterialsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px dashed ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.common.white,
  },
}));

interface BidAreaOptionProps {
  area: IMergedBidArea;
  areaType: IAreaTypeResponse | undefined;
  planOptionMaterials: IMaterialsResponse[];
  isPreview: boolean;
  isEditingOption?: boolean;
  checkedAreas: IMergedBidArea[];
  showCategoryFilter: boolean;
  openAreaDrawer: boolean;
  showLinkedItems: boolean;
  linkedMaterialDetails: IMaterialsResponse[];
  optionData: IBidOptionResponse;
  setOptionData: React.Dispatch<React.SetStateAction<IBidOptionResponse>>;
  handleOpenItemDrawer?: (area: IMergedBidArea) => void;
  handleOpenReplaceItemModal: (params: {
    item: IMaterialsResponse;
    area: IMergedBidArea;
  }) => void;
}
